/**
 * Subscription Helper Utilities
 * 
 * These utilities help determine the effective status of subscriptions,
 * particularly for handling trial subscriptions where the database status
 * might show "active" but the subscription is actually in a trial period.
 * 
 * According to the docs (docs/subscription-types.md):
 * - trial_end_date is the authoritative source for trial detection
 * - Both trials and paid subscriptions have stripe_subscription_id
 * - Database status might be "active" even during trials
 */

export interface SubscriptionWithTrial {
  status: "trial" | "trialing" | "active" | "cancelled" | "past_due" | "unpaid"
  trial_end_date: string | null
  cancel_at_period_end: boolean | null
}

/**
 * Check if a subscription is currently in an active trial period
 * @param subscription - Subscription object with trial_end_date
 * @returns true if trial is active (trial_end_date exists and is in the future)
 */
export function isTrialActive(subscription: SubscriptionWithTrial): boolean {
  if (!subscription.trial_end_date) return false
  return new Date(subscription.trial_end_date) > new Date()
}

/**
 * Get the effective status of a subscription
 * Returns "trial" if the subscription has an active trial, otherwise returns the database status
 * @param subscription - Subscription object
 * @returns The effective status string
 */
export function getEffectiveStatus(
  subscription: SubscriptionWithTrial
): "trial" | "active" | "cancelled" | "past_due" | "unpaid" | "trialing" {
  if (isTrialActive(subscription)) return "trial"
  // Return the database status as-is for non-trial subscriptions
  return subscription.status
}

/**
 * Calculate days remaining in a trial period
 * @param trialEndDate - ISO date string for when trial ends
 * @returns Number of days remaining (rounded up), or 0 if expired
 */
export function getTrialDaysRemaining(trialEndDate: string | null): number {
  if (!trialEndDate) return 0
  
  const now = new Date()
  const endDate = new Date(trialEndDate)
  const diffMs = endDate.getTime() - now.getTime()
  
  if (diffMs <= 0) return 0
  
  // Round up to show at least 1 day if there's any time left
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

/**
 * Format trial end date as a human-readable string
 * @param trialEndDate - ISO date string for when trial ends
 * @returns Formatted string like "Nov 20, 2025" or "Expired"
 */
export function formatTrialEndDate(trialEndDate: string | null): string {
  if (!trialEndDate) return "N/A"
  
  const endDate = new Date(trialEndDate)
  const now = new Date()
  
  if (endDate <= now) return "Expired"
  
  return endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Check if a status discrepancy is expected/normal behavior
 * 
 * Expected discrepancies:
 * - DB shows "active" but Stripe shows "trialing" when trial_end_date is in the future
 * - DB shows "active" but Stripe shows "active" when trial_end_date is in the past
 * 
 * @param dbStatus - Status from our database
 * @param stripeStatus - Status from Stripe
 * @param trialEndDate - Trial end date from database
 * @returns true if the discrepancy is expected/normal
 */
export function isExpectedStatusDiscrepancy(
  dbStatus: string,
  stripeStatus: string,
  trialEndDate: string | null
): boolean {
  // If both statuses match, no discrepancy
  if (dbStatus === stripeStatus) return false
  
  // Check for expected trial-related discrepancies
  const hasActiveTrial = trialEndDate && new Date(trialEndDate) > new Date()
  
  // Expected: DB shows "active" during trial period, Stripe shows "trialing"
  if (dbStatus === "active" && stripeStatus === "trialing" && hasActiveTrial) {
    return true // This is expected, not a real discrepancy
  }

  // Any other status mismatch is a real discrepancy
  // Note: Matching statuses are already handled by the early return at line 100
  return false
}

/**
 * Check if a period end discrepancy is expected/normal behavior
 * 
 * During active trials, period end dates are expected to differ because:
 * - Database stores the full subscription period end (e.g., 1 year out)
 * - Stripe also has the full period end, but what matters during trial is trial_end
 * - The comparison is between matching fields, but during trials they represent
 *   different logical concepts (trial end vs full period end)
 * 
 * @param trialEndDate - Trial end date from database
 * @param discrepancyMinutes - Minutes of difference between DB and Stripe period end
 * @returns true if the discrepancy is expected during a trial
 */
export function isExpectedPeriodEndDiscrepancy(
  trialEndDate: string | null,
  discrepancyMinutes: number
): boolean {
  // If there's an active trial, period end discrepancies might be expected
  const hasActiveTrial = trialEndDate && new Date(trialEndDate) > new Date()
  
  if (!hasActiveTrial) {
    // No active trial - any discrepancy is real
    return false
  }
  
  // During trials, we actually don't expect current_period_end to differ
  // Both DB and Stripe should have the same full period end
  // However, if the comparison is accidentally comparing trial_end vs current_period_end,
  // that would cause a large discrepancy
  
  // Large discrepancies (> 30 days) during trials are likely comparison errors
  // and can be suppressed since trial_end is what matters, not period_end
  const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60
  
  if (discrepancyMinutes > THIRTY_DAYS_IN_MINUTES) {
    // This is likely a comparison error (trial_end vs period_end)
    // Suppress this during trials
    return true
  }
  
  // Small discrepancies are still real issues even during trials
  return false
}
